import { useEffect } from "react";

import { useLocation, useNavigate } from "utils";

import { SOIL_SIGHT_FLOW_ROUTES, SoilSightFlowRoute } from "consts";

import { useSoilSightOnboardingAccess } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingAccess";
import { resolveSoilSightFlowRoute } from "pages/app/scenario-onboarding/utils/flowRoutes";

/** Redirects away from `currentRoute` when the flow belongs elsewhere; `isHolding` = render nothing. */
export const useSoilSightFlowRedirect = (currentRoute: SoilSightFlowRoute): { isHolding: boolean } => {
  const access = useSoilSightOnboardingAccess();
  const navigate = useNavigate();
  const location = useLocation();

  const targetRoute = resolveSoilSightFlowRoute(access);
  // Leaving on purpose (X / step-1 Back) must not be bounced back by the resume-at-step-1 rule.
  const isDeliberateLandingVisit =
    currentRoute === SOIL_SIGHT_FLOW_ROUTES.landing && (location.state as { fromOnboarding?: boolean } | null)?.fromOnboarding === true;
  const isSuppressedResume = isDeliberateLandingVisit && targetRoute === SOIL_SIGHT_FLOW_ROUTES.onboardingStep1;
  const redirectTo = targetRoute && targetRoute !== currentRoute && !isSuppressedResume ? targetRoute : null;
  // We forward the state, so the landing still knows the entry point after the /scenarios hop.
  const forwardedState = location.state ?? undefined;

  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true, state: forwardedState });
    }
  }, [redirectTo, navigate, forwardedState]);

  return { isHolding: access.isLoading || redirectTo !== null };
};
